import { Book, BuildStatus, Nullable } from '@Models';
import { BookRepository, RequestBookSuccess } from '@Repository/publication';
import { inject, injectable } from 'inversify';
import { TYPES } from '@Types';
import { Locale } from '@Repository/locale';
import { RequestAction } from '@Repository/RequestAction';
import { Result } from '@Redux/type';
import { DeleteRepository } from '@Repository/DeleteRepository';
import toast from 'react-hot-toast';
import { ArmyAction } from '@Redux/actions';
import { IEditableEntityAction } from '@Redux/IEditableEntityAction';
import { PayloadAction } from '@reduxjs/toolkit';
import myBookSlice from '@Redux/reducers/publication/book.slice';
import { BookActionState } from '@Stores/publication/book.state';

@injectable()
export class BookAction extends IEditableEntityAction<
  string,
  Book,
  RequestBookSuccess
> {
  @inject(TYPES.Repository.BookRepository)
  private _repository!: BookRepository;
  @inject(TYPES.Locale)
  private _locale!: Locale;
  @inject(TYPES.Actions.ArmyAction)
  private _armyAction!: ArmyAction;

  public cancel(): PayloadAction {
    return myBookSlice.actions.cancel();
  }

  public requestEdit(): PayloadAction<BookActionState> {
    return myBookSlice.actions.setRequestAction(BookActionState.Edit);
  }

  public requestDelete(): PayloadAction<BookActionState> {
    return myBookSlice.actions.setRequestAction(BookActionState.Delete);
  }

  public requestCreationBook(): PayloadAction<BookActionState> {
    return myBookSlice.actions.setRequestAction(BookActionState.Creation);
  }

  requestBuild(): PayloadAction<BookActionState> {
    return myBookSlice.actions.setRequestAction(BookActionState.Build);
  }

  requestClone(): PayloadAction<BookActionState> {
    return myBookSlice.actions.setRequestAction(BookActionState.Clone);
  }

  public create =
    (book: Book): Result<void> =>
    async (dispatch) => {
      dispatch(this.areLoading(true));

      const response = await this._repository.create(book, { locale: null });

      if (this.handleFailure(response, dispatch)) return;

      const success = response as RequestBookSuccess;
      window.location.href = `/${this._locale.getlocale()}/publication/books/${
        success.payload.id
      }`;
    };

  getDeleteRepository(): DeleteRepository<string> {
    return this._repository;
  }

  public moveSection =
    (sectionId: string, source: number, destination: number): Result<void> =>
    async (dispatch, getState) => {
      dispatch(this.areLoading(true));

      const state = getState();

      const book = state.book.entity;
      if (!book) return;

      console.log(
        `sectionId: ${sectionId}, source: ${source}, destination: ${destination}`,
      );

      book.sections = book.sections
        .map((item) => {
          if (item.id === sectionId) {
            item.position = destination;
          } else if (item.position >= source && item.position <= destination) {
            item.position -= 1;
          } else if (item.position <= source && item.position >= destination) {
            item.position += 1;
          }
          return item;
        })
        .sort((a, b) => a.position - b.position);

      dispatch(this.fetchDataSuccess(book));
      dispatch(this.areLoading(false));
    };

  public build =
    (bookId: string): Result<void> =>
    async (dispatch, getState) => {
      dispatch(this.areLoading(true));

      const state = getState();
      const book = state.book.entity;
      if (!book) return;

      const response = await this._repository.build(bookId, { locale: null });

      if (this.handleFailure(response, dispatch)) return;

      toast.success('Start build of the book');
      book.buildStatus = BuildStatus.Building;

      dispatch(this.fetchDataSuccess(book));
      dispatch(this.areLoading(false));
    };

  protected fetchDataSuccess(item: Book): PayloadAction<Book> {
    return myBookSlice.actions.setMyBook(item);
  }

  protected cacheDataSuccess(item: Book): PayloadAction<Book> {
    return myBookSlice.actions.setMyBook(item);
  }

  protected haveError(message: string): PayloadAction<string> {
    return myBookSlice.actions.setError(message);
  }

  protected areLoading(bool: boolean): PayloadAction<boolean> {
    return myBookSlice.actions.setLoading(bool);
  }

  protected async getCachedItem({
    entityId,
    locale,
  }: {
    entityId: string;
    locale: Nullable<string>;
  }): Promise<Nullable<Book>> {
    return await this._repository.getCached(entityId, {
      locale: locale ?? null,
    });
  }

  protected async getItem({
    entityId,
    locale,
  }: {
    entityId: string;
    locale: Nullable<string>;
  }): Promise<RequestAction> {
    return await this._repository.get(entityId, { locale: locale ?? null });
  }

  protected subFetchDataBefore =
    ({ entity }: { entity: Book }): Result<void> =>
    (dispatch) => {
      dispatch(this._armyAction.fetchData({ entityId: entity.armyId }));
    };

  protected subFetchDataAfter =
    ({
      entity,
      cachedItem,
    }: {
      entity: Book;
      cachedItem: Nullable<Book>;
    }): Result<void> =>
    (dispatch) => {
      if (!cachedItem || cachedItem?.armyId != entity.armyId) {
        dispatch(this._armyAction.fetchData({ entityId: entity.armyId }));
      }
    };
}
